import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Plus } from "lucide-react";
import { Badge, Button, ErrorBanner } from "@lumanity/ui/primitives";
import OutputPickerDialog from "./OutputPickerDialog";
import { useProjectOutputs } from "./outputsApi";
import { OUTPUT_STAGE_LABELS, OUTPUT_TYPE_LABELS, outputPath } from "./model";

const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** The project's outputs (abstracts, manuscripts, posters), newest first, and the "New output" entry point. */
const ProjectOutputs = ({ projectId, kantataProjectId }: { projectId: string; kantataProjectId?: string | null }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data: outputs = [], isLoading, error } = useProjectOutputs(projectId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">Each output is written against one Kantata task.</p>
        <Button className="bg-brand-teal hover:bg-brand-teal/90 text-white" onClick={() => setPickerOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> New output
        </Button>
      </div>

      {error ? (
        <ErrorBanner message={`Could not load outputs: ${error.message}`} />
      ) : isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Loading outputs...</div>
      ) : outputs.length === 0 ? (
        <div className="bg-card border border-dashed text-muted-foreground py-10 text-center">
          No outputs yet. Start one with <span className="font-medium text-brand-navy">New output</span>.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {outputs.map((o) => (
            <Link
              key={o.id}
              to={outputPath(projectId, o.id)}
              className="group flex items-start justify-between gap-3 border bg-card px-5 py-4 hover:shadow-md transition-shadow"
            >
              <div className="min-w-0 space-y-1.5">
                <div className="flex items-center gap-2">
                  <Badge className="bg-brand-navy text-white hover:bg-brand-navy">{OUTPUT_TYPE_LABELS[o.type] ?? o.type}</Badge>
                  <Badge variant="outline">{OUTPUT_STAGE_LABELS[o.stage] ?? o.stage}</Badge>
                </div>
                <h3 className="font-semibold text-brand-navy group-hover:text-brand-teal transition-colors truncate">
                  {o.title || "Untitled output"}
                </h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    Kantata task <span className="font-medium text-brand-navy">{o.kantata_task_title}</span>
                  </span>
                  {o.outlet && <span>{o.outlet.name}</span>}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Updated {formatDate(o.updated_at)}
                  </span>
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 shrink-0 text-brand-navy group-hover:text-brand-teal transition-colors" />
            </Link>
          ))}
        </div>
      )}

      <OutputPickerDialog projectId={projectId} kantataProjectId={kantataProjectId} open={pickerOpen} onOpenChange={setPickerOpen} />
    </div>
  );
};

export default ProjectOutputs;
